import * as React from 'react'
import withHydrationOnDemand from 'react-hydration-on-demand'
import { useMutation } from '@apollo/react-hooks'
import loadable from '@loadable/component'
import dynamic from 'next/dynamic'
import { vsprintf } from 'sprintf-js'

import { Constituent } from '@thg-commerce/enterprise-aurora-product/resolvers/ProductPage/types'
import { FulfilmentMethodIconsRenderer } from '@thg-commerce/enterprise-components/FulfilmentMethods/FulfilmentMethodIconsRenderer'
import {
  PersonalisationToggle,
  usePersonalisationToggleContext,
} from '@thg-commerce/enterprise-components/Personalisation'
import {
  PersonalisationData,
  PersonalisationForm,
} from '@thg-commerce/enterprise-components/Personalisation/PersonalisationForm'
import { useQuickBuyClickHandler } from '@thg-commerce/enterprise-components/Product/src/ProductBlock/hooks'
import { WishlistButtonType } from '@thg-commerce/enterprise-components/Product/src/WishlistButton/WishlistButton'
import { SubscriptionPaymentType } from '@thg-commerce/enterprise-components/ProductVariations'
import { VariantFields as TransformerVariant } from '@thg-commerce/enterprise-components/src/transformer/productOptionsTransformer'
import {
  BasketContext,
  EnterpriseContext,
  i18n,
  Routes,
  useAddToBasket,
  useEnterpriseContext,
  useLogger,
  useRequestConfig,
  useSessionSettings,
  useSiteConfig,
  useSiteDefinition,
  useTransmit,
} from '@thg-commerce/enterprise-core'
import { InteractionLocation } from '@thg-commerce/enterprise-core/src/Basket'
import { useBasket } from '@thg-commerce/enterprise-core/src/Basket/hooks/useBasketId'
import { getMaxQuantity } from '@thg-commerce/enterprise-core/src/Basket/QuickBuyModalPresenter/utils'
import { MutationAddPersonalisedProductToBasketArgs } from '@thg-commerce/enterprise-graphql/horizon-schema'
import { getColumboEventData } from '@thg-commerce/enterprise-metrics'
import { ProductPageData } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductPage'
import {
  FreeTextProductPersonalisationField,
  SingleSelectionProductPersonalisationField,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Product'
import {
  Country,
  Currency,
  Feature,
  FulfilmentMethod,
  InStockLocation,
  MutationAddProductToBasketWithSubscriptionContractArgs,
  SubscriptionContract,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { ChoiceMap } from '@thg-commerce/enterprise-network/src/transformers/product/options'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'
import {
  Option,
  ProductOptionRenderer,
  SelectedOptions,
} from '@thg-commerce/enterprise-product-options/src'
import { TransformedOption } from '@thg-commerce/enterprise-product-options/src/transformers/option'
import {
  ChoiceSelectedVariantMap,
  ProductOptionsMap,
} from '@thg-commerce/enterprise-product-options/src/transformers/productOptionsMap'
import { VariantOptions } from '@thg-commerce/enterprise-product-options/src/types'
import { getPrice } from '@thg-commerce/enterprise-product-options/src/utils/price'
import { spacing, styled, Text } from '@thg-commerce/enterprise-theme'
import { calculateDiscount } from '@thg-commerce/enterprise-utils'
import {
  Button,
  PlatformMessage,
  Price,
  QuantitySelector,
  QuantitySelectorProps,
  SafeHtml,
  Separator,
} from '@thg-commerce/gravity-elements'
import { SwatchShape } from '@thg-commerce/gravity-elements/Swatch/types'

import { FreeDeliveryIndicator } from '../../../../components'
import { AddToBasketButton } from '../../../../components/AddToBasketButton/AddToBasketButton'
import {
  HullabalookButton,
  StyledHangerIcon,
} from '../../../../components/Hullabalook/HullabalookButton'
import { PriceContainer } from '../../../../components/ProductSummary/styles'
import { PushNotifyWhenInStock } from '../../../../components/PushNotifyWhenInStock/PushNotifyWhenInStock'
import { AddPersonalisedProductToBasket as ADD_TO_BASKET_PERSONALISED_PRODUCTS } from '../../../../graphql/AddPersonalisedProductToBasket.graphql'
import { AddProductToBasketWithSubscriptionContract as ADD_TO_BASKET_WITH_CONTRACT } from '../../../../graphql/AddProductToBasketWithSubscriptionContract.graphql'
import { WishlistButtonRendererProps } from '../../../WishlistButtonRenderer'
import {
  AddToBasketContainer,
  QuantityPlatformMessage,
  QuantitySelectorContainer,
  QuantityText,
  StyledButtonContainer,
  WishlistButtonWrapper,
} from '../styles'

import { AuroraPurchaseOptions } from './AuroraPurchaseOptions'

const ExternalLink = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/ExternalLink'),
)

const SESSION_STORAGE_KEY = 'personalisationToggleState'

const ClickAndCollectButtonRendererComponent = loadable(
  () =>
    import('../../../ClickAndCollectButtonRenderer').then(
      (mod) => mod.ClickAndCollectButtonRenderer,
    ),
  {
    ssr: true,
  },
)

const ClickAndCollectButtonRenderer = withHydrationOnDemand({
  on: [['visible']],
})(ClickAndCollectButtonRendererComponent)

const WishlistButtonRenderer = dynamic<WishlistButtonRendererProps>(() =>
  import('../../../WishlistButtonRenderer').then(
    (mod) => mod.WishlistButtonRenderer,
  ),
)

const OutOfStockText = styled(SafeHtml)`
  ${Text('bodyText', 'default')}
  margin-bottom: ${spacing(2)};
`

const SubscriptionInfoMessage = styled(PlatformMessage)`
  margin-bottom: ${spacing(2)};
`

const ConstituentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing(4)};
  margin-top: ${spacing(4)};
`

const StyledButton = styled(Button)`
  width: 100%;
  white-space: nowrap;
  height: 48px;
`

const ShopNowContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing(1)};
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 18px;
    height: 18px;
  }
`

export const InfoText = styled.p`
  ${Text('bodyText', 'default')};
`

const getConstituentProductCta = (
  i18nText: { buyNowText: string; infoMessage: string },
  constituentProduct?: Constituent,
) => {
  if (
    !constituentProduct?.externalLinkedUrl ||
    !constituentProduct?.isConstituentProduct
  ) {
    return null
  }

  const handleClick = () => {
    if (constituentProduct.externalLinkedUrl) {
      window.location.href = constituentProduct.externalLinkedUrl
    }
  }

  return (
    <React.Fragment>
      <ConstituentsWrapper>
        <StyledButton onClick={handleClick}>{i18nText.buyNowText}</StyledButton>

        <ShopNowContainer>
          <IconWrapper>
            <ExternalLink viewBox="0 0 15 15" />
          </IconWrapper>

          <InfoText>{i18nText.infoMessage}</InfoText>
        </ShopNowContainer>
      </ConstituentsWrapper>
    </React.Fragment>
  )
}

export interface ProductOptionSelectorProps {
  initialOptions: Option[]
  defaultSelectedOptions: string[]
  enableQuantity: boolean
  enableMobilePDPStickyAddToBasket?: boolean
  isFirstProductRecommendation?: boolean
  enablePriceOnMobilePDPStickyAddToBasket?: boolean
  masterProductSku: number
  sortedOptionKeys: string[]
  queryFullVariantData: boolean
  product?: ProductPageData
  forceDropDowns?: boolean
  swatchShape?: SwatchShape
  showSwatches?: boolean
  notifyWhenInStock?: boolean
  labelCustomComponent?: React.ReactNode
  preselectVariant: boolean
  linkedOn?: string
  personalisationImages?: ChoiceSelectedVariantMap | VariantOptions
  enableSubscribeAndSaveInlinePricing?: boolean
  setActiveTabIndex: (state: number) => void
  activeTabIndex: number
  storeStockActive?: boolean
  setStoreStockActive?: (value: boolean) => void
}

type AddBasketWithContractType = {
  addProductToBasketWithSubscriptionContract: {
    id: string
  }
}

type AddBasketPersonalisedProduct = {
  addPersonalisedProductToBasket: {
    id: string
  }
}

const INITIAL_QUANTITY = 1
const INVALID_CONTRACT_ID = '15'

const getAddToBasketText = ({
  inStock,
  addToBasketButton,
  preorder,
  personalisedProduct,
}: {
  inStock?: boolean
  addToBasketButton: {
    addToBasketText: string
    outOfStockText: string
    preorderNowText: string
    personaliseProductText: string
  }
  preorder?: boolean
  personalisedProduct?: boolean
}) => {
  if (!inStock) {
    return addToBasketButton.outOfStockText
  }

  if (personalisedProduct) {
    return addToBasketButton.personaliseProductText
  }

  return preorder
    ? addToBasketButton.preorderNowText
    : addToBasketButton.addToBasketText
}

interface Variant extends TransformerVariant {
  notifyWhenInStockEnabled?: boolean | null
  disableColorDropdown?: boolean
  options: Option[]
  enableAddToBasket?: boolean | null
  personalisationFields?: [
    | FreeTextProductPersonalisationField
    | SingleSelectionProductPersonalisationField,
  ]
}

// TODO REBUILD-8952 Reduce complexity
// eslint-disable-next-line complexity
export const ProductOptionSelector = (
  props: ProductOptionSelectorProps & {
    variants: Variant[]
    selectedVariant: Variant
    defaultVariant?: Variant
    // TODO REBUILD-8692 fix type
    variantChangedCallback: (variant: any) => void
    selectedOptions: SelectedOptions
    setSelectedOptionsCallback?: (selectedOptions: SelectedOptions) => void
    choiceMap?: ChoiceMap
    disableColorDropdown?: boolean
    parentSku?: string
    optionsMap?: ProductOptionsMap
    setOptionsMap?: (optionsMap: ProductOptionsMap) => void
  },
) => {
  const transmit = useTransmit()
  const EnterpriseCtx = React.useContext(EnterpriseContext)
  const {
    isPersonalisationActive,
    setIsPersonalisationActive,
    variantSku,
    setVariantSku,
    personalisationFields,
    setPersonalisationFields,
    setPersonalisationImagesContext,
  } = usePersonalisationToggleContext()
  const {
    domain,
    defaultLocale,
    defaultCurrency,
    subsite,
    siteId,
    channel,
  } = useSiteDefinition()
  const propertyArgs = {
    defaultLocale,
    subsite,
    siteId,
    channel,
    customerLocation: EnterpriseCtx.currentLocation,
  }
  const { isMobileApp } = useRequestConfig()
  const { currency, shippingDestination } = useSessionSettings()
  const {
    enablePushNotifyWhenInStock,
    enableClickAndCollect,
    enableProductQuantityInStockAvailability,
    papMessageTopPosition,
    displayPriceAboveQuantity,
    showOOSMessagePDP,
    enableMobilePDPStickyAddToBasket,
    enableQuickbuyOnlyOnMobile,
    enableProductPersonalisationInterface,
    useGA4EnhancedEcom,
    useExternalIdentifier,
    useExternalIdentifierInSchema,
    showHullabalookRecommendations,
    hullabalookRecsExperienceLabel,
    showHullablookCTAButton,
    enablePDPStoreStock,
    hasShoeSizeMeId,
  } = useSiteConfig()
  const { basket } = useBasket()
  const { horizonFeatures } = useEnterpriseContext()
  const { execute: addToBasket } = useAddToBasket({
    forceAddToBasket: true,
  })
  const theme = useTheme()
  const logger = useLogger()
  const [optedInVariants, setOptedInVariants] = React.useState<{
    [key: string]: boolean
  }>({})
  const [displayError, setDisplayError] = React.useState<boolean>(false)
  const LoyaltyEnabled = horizonFeatures?.includes(Feature.Loyalty)

  React.useEffect(() => {
    if (enablePDPStoreStock) {
      if (!props.selectedVariant?.enableAddToBasket && props.storeStockActive) {
        setDisplayError(true)
        return
      }
      setDisplayError(false)
    }
  }, [
    props.storeStockActive,
    props.selectedVariant?.enableAddToBasket,
    enablePDPStoreStock,
  ])

  const [selectedFrequencyId, setSelectedFrequencyId] = React.useState<string>(
    INVALID_CONTRACT_ID,
  )

  const basketProductSku = basket?.items.find(
    (item) => item.product.sku === props.selectedVariant?.sku,
  )?.subscriptionContract

  React.useEffect(() => {
    const initialFrequencyId =
      basketProductSku?.id ??
      props.selectedVariant?.subscriptionContracts?.[0]?.id ??
      INVALID_CONTRACT_ID

    setSelectedFrequencyId(initialFrequencyId)
  }, [props.selectedVariant])

  const addToBasketButtonContainerRef = React.useRef<HTMLDivElement>(null)
  const [addToBasketIsSticky, setAddToBasketIsSticky] = React.useState<boolean>(
    false,
  )
  const [hasScrolled, setHasScrolled] = React.useState<boolean>(false)

  const [selectedOptions, setSelectedOptions] = React.useState(
    props.selectedOptions,
  )

  const [options, setOptions] = React.useState<TransformedOption[]>(
    props.initialOptions,
  )

  const [isVariantsUpdatedForGA4, updateVariantsForGA4] = React.useState<
    boolean
  >(false)

  const setSelectedOptionsCallback = (selectedOptions) => {
    setSelectedOptions(selectedOptions)
  }

  const setOptionsCallback = (options) => {
    setOptions(options)
  }

  React.useEffect(() => {
    const containerRef = addToBasketButtonContainerRef.current

    const observer = new IntersectionObserver(
      ([element], _) => {
        if (element && element.isIntersecting) {
          setAddToBasketIsSticky(false)
        } else {
          setAddToBasketIsSticky(true)
        }
      },
      {
        rootMargin: `0px`,
        threshold: 1,
      },
    )
    containerRef && observer.observe(containerRef)

    return () => {
      containerRef && observer.unobserve(containerRef)
    }
  }, [])

  const getInitialPersonalisationTabIndex = (theme) => {
    return theme.pageTheme.productPersonalisation?.showClassicContent ? 0 : 1
  }

  const [
    activePersonalisationTabIndex,
    setActivePersonalisationTabIndex,
  ] = React.useState<number>(getInitialPersonalisationTabIndex(theme))

  const isPersonalisedProduct = !!props.selectedVariant?.personalisationFields
    ?.length

  const [personalisationData, setPersonalisationData] = React.useState<
    PersonalisationData
  >({})

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setHasScrolled(true)
        window.removeEventListener('scroll', handleScroll)
      }
    }
    if (props.enableMobilePDPStickyAddToBasket) {
      window.addEventListener('scroll', handleScroll)
    }
    return () => window.removeEventListener('scroll', handleScroll)
  })

  const useSessionStorage = (
    isPersonalisationActive,
    variantSku,
    personalisationFields,
  ) => {
    React.useEffect(() => {
      if (typeof window !== 'undefined') {
        const stateToStore = {
          isPersonalisationActive,
          variantSku,
          personalisationFields,
        }
        sessionStorage.setItem(
          SESSION_STORAGE_KEY,
          JSON.stringify(stateToStore),
        )
      }
    }, [isPersonalisationActive, variantSku, personalisationFields])
  }

  const optInForCurrentVariant =
    !props.selectedVariant?.inStock &&
    props.selectedVariant?.notifyWhenInStockEnabled

  const showSetupPushNotificationText =
    enablePushNotifyWhenInStock &&
    isMobileApp &&
    props.product?.hasVariantsWithNotifyWhenInStockEnabled

  const pushNotification = Boolean(
    enablePushNotifyWhenInStock && isMobileApp && optInForCurrentVariant,
  )

  const addToBasketWishlist =
    theme.pageTheme?.productOptions.addToBasketContainer.addToWishlistButton
      .show

  const wishlistButtonStyle =
    theme.pageTheme?.productOptions.addToBasketContainer.addToWishlistButton
      ?.style

  // TODO REBUILD-8913 remove Fulfilment varibles in favour of transformer
  const clickAndCollectAvailable = Boolean(
    props.selectedVariant?.eligibleForFulfilmentMethods?.includes(
      FulfilmentMethod.CollectInStore,
    ),
  )

  const homeDeliveryAvailable = props.selectedVariant
    ?.eligibleForFulfilmentMethods
    ? Boolean(
        props.selectedVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.HomeDelivery,
        ) &&
          props.selectedVariant?.inStockLocations?.includes(
            InStockLocation.Warehouse,
          ),
      )
    : true

  const showQuantity =
    props.enableQuantity &&
    (enableProductQuantityInStockAvailability
      ? homeDeliveryAvailable ||
        clickAndCollectAvailable ||
        props.selectedVariant?.isCheckStock
      : true)

  const i18nText = {
    offLabel: i18n('general.off.text'),
    rrpLabel: i18n('general.rrp.text'),
    saveLabel: i18n('general.save.text'),
    quantitySelector: {
      increaseButtonLabel: i18n('general.quantity.increase'),
      decreaseButtonLabel: i18n('general.quantity.decrease'),
      quantityLabel: i18n('general.quantity.text'),
    },
    missingSizeError: i18n('product.missing.size.error'),
    addToWishListButton: {
      addToWishListText: i18n('product.addtowishlistbutton.addtowishlist.text'),
    },
    addToBasketButton: {
      addToBasketText: i18n('product.optionselector.addtobasket.text'),
      outOfStockText: i18n('product.optionselector.outofstock.text'),
      preorderNowText: i18n('listitem.button.preordernow.text'),
      personaliseProductText: i18n(
        'product.optionselector.personalisedproduct.text',
      ),
    },
    wishlistTooltipButton: {
      closeTooltip: i18n('product.addtowishlist.tooltip.button.close.text'),
    },
    wishlistTooltipContent: {
      loginSignupText: i18n('product.addtowishlist.tooltip.loginsignup.text'),
      toUseWishlistText: i18n('product.addtowishlist.tooltip.text'),
    },
    basketQuantity: {
      limit: i18n('product.maxquantity.basket.limit.text'),
      singleItemText: i18n('product.maxquantity.basket.singleitem.text'),
      multipleItemsText: i18n('product.maxquantity.basket.multipleitems.text'),
    },
    outOfStock: {
      outOfStocktext: i18n('product.productoptions.dropdown.unavailable.text'),
      optInText: i18n('product.outofstocknotification.optin.text'),
      optedInText: i18n('product.outofstocknotification.optedin.text'),
    },
    productWithSubscriptionContracts: i18n(
      'product.inbasketwith.subscription.contracts',
    ),
    alreadyInBasketAsSubscription: i18n(
      'product.alreadyaddedinbasket.info.text',
    ),
    clickAndCollect: {
      clickAndCollectButtonText: i18n(
        'product.optionselector.clickandcollect.button.text',
      ),
      clickAndCollectLinkText: i18n(
        'product.optionselector.clickandcollect.link.text',
      ),
      clickAndCollectCheckStoreStockText: i18n(
        'product.optionselector.clickandcollect.button.checkstorestock.text',
      ),
      isAvailable: i18n('product.delivery.store.available.text'),
      isNotAvailable: i18n('product.clickandcollect.unavailable.text'),
    },
    decreaseButtonLabelDynamic: i18n(
      'general.quantity.decrease.dynamic',
      props.selectedVariant?.title,
    ),
    increaseButtonLabelDynamic: i18n(
      'general.quantity.increase.dynamic',
      props.selectedVariant?.title,
    ),
    homeDelivery: {
      isAvailable: i18n('product.delivery.home?.inStock.text'),
      isNotAvailable: i18n('product.delivery.home.unavailable.text'),
      isOutOfStock: i18n('product.delivery.home.outofstock.text'),
      datedDelivery: i18n(
        'basket.item.fulfilment.leadtime',
        props.selectedVariant?.leadTime?.toString(),
      ),
      nextDayDelivery: i18n('basket.item.fulfilment.nextdaydelivery'),
      oneManDelivery: i18n(
        'product.item.fulfilment.1man.nextdaydeliveryavailable',
      ),
      outOfGaugeDelivery: i18n(
        'product.item.fulfilment.outofgauge.nameddaydeliveryavailable',
      ),
      dynamicDelivery: i18n(
        `product.item.fulfilment.pdp.${props.selectedVariant?.weightGroups?.[0]?.toLowerCase()}`,
      ),
    },
    storeDelivery: {
      isAvailable: i18n('product.delivery.store.available.text'),
    },
    orderInStore: {
      isAvailable: i18n('product.item.fulfilment.orderinstore.available.text'),
      message: i18n('product.item.fulfilment.orderinstore.available.message'),
    },
    pdpDeliveryMessage: {
      homeDeliveryMsg1Man: i18n('product.item.fulfilment.1man.secondmessage'),
      homeDeliveryMsgOutOfGauge: i18n(
        'product.item.fulfilment.outofgauge.secondmessage',
      ),
      storeMsg: i18n('product.storepickup.cost'),
    },
    buildYourOufitMessage: i18n('product.item.build.your.outfit.text'),
    buyNow: i18n('general.productbuynow.text'),
    buyNowInfoMessage: i18n('general.redirectshopnow.warning'),
  }
  // TODO REBUILD-8913 remove for transformer
  const fulfilmentMethodIconsProps = {
    enableClickAndCollect,
    inStockLocations: props.selectedVariant?.inStockLocations,
    inStock: Boolean(props.selectedVariant?.inStock),
    isCheckStock: Boolean(props.selectedVariant?.isCheckStock),
    isOrderInStore: Boolean(props.selectedVariant?.isOrderInStore),
    leadTime: props.selectedVariant?.leadTime,
    weightGroups: props.selectedVariant?.weightGroups,
    isBookable: Boolean(props.selectedVariant?.isBookable),
    iconsAvailability: {
      homeDelivery: Boolean(
        props.selectedVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.HomeDelivery,
        ),
      ),
      storeAvailable: Boolean(
        props.selectedVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.CollectInStore,
        ),
      ),
      storeDelivery: Boolean(
        props.selectedVariant?.eligibleForFulfilmentMethods?.includes(
          FulfilmentMethod.DeliverToStore,
        ),
      ),
    },
    i18nText: {
      offLabel: i18nText.offLabel,
      clickAndCollect: i18nText.clickAndCollect,
      homeDelivery: i18nText.homeDelivery,
      storeDelivery: i18nText.storeDelivery,
      orderInStore: i18nText.orderInStore,
      pdpDeliveryMessage: i18nText.pdpDeliveryMessage,
    },
    missingSizeError: i18nText.missingSizeError,
  }

  const [selectedQuantity, setSelectedQuantity] = React.useState(
    INITIAL_QUANTITY,
  )

  const notificationOptInText = optedInVariants[props.selectedVariant?.sku]
    ? i18nText.outOfStock.optedInText
    : vsprintf('<strong>%s%s</strong> %s', [
        i18nText.outOfStock.outOfStocktext,
        !props.selectedVariant?.inStock ? ' -' : '?',
        i18nText.outOfStock.optInText,
      ])

  const updateProductVariants = (params: {
    sku: string
    isOptedIn: boolean
  }) => {
    const nextState = { ...optedInVariants }
    nextState[params.sku] = params.isOptedIn
    setOptedInVariants(nextState)
  }

  const personalisationUrl = `https://${domain}${Routes.PersonaliseYourBox}?productId=${props.selectedVariant?.sku}`

  const [personalisationImages, setPersonalisationImages] = React.useState<
    ChoiceSelectedVariantMap | VariantOptions
  >()

  const { presentAddedToBasketModal, basketIdRef } = React.useContext(
    BasketContext,
  )

  const { maxQuantity, quantityInBasket } = getMaxQuantity(
    basket,
    props.selectedVariant,
  )

  const maxQuantityBasketItemMessageText = vsprintf(
    quantityInBasket > 1
      ? i18nText.basketQuantity.multipleItemsText
      : i18nText.basketQuantity.singleItemText,
    [props.selectedVariant?.maxPerOrder || 5000, quantityInBasket],
  )

  const quantitySelectorProps: QuantitySelectorProps = {
    i18nText: {
      increaseButtonLabel: i18nText.quantitySelector.increaseButtonLabel,
      decreaseButtonLabel: i18nText.quantitySelector.decreaseButtonLabel,
      quantityLabel: i18nText.quantitySelector.quantityLabel,
      decreaseButtonLabelDynamic: i18nText.decreaseButtonLabelDynamic,
      increaseButtonLabelDynamic: i18nText.increaseButtonLabelDynamic,
    },
    productTitle: props.selectedVariant?.title,
    quantity: selectedQuantity,
    minValue: 1,
    maxValue: maxQuantity,
    editable: true,
    quantityChangedCallback: (quantity) => {
      quantity && setSelectedQuantity(quantity)
    },
  }

  const onColourSwatchClick = () => {
    transmit({
      type: 'columbo',
      payload: getColumboEventData({
        propertyArgs,
        argumentsObj: {
          '0': 'Accessibility',
          '1': 'Clicked',
          '2': 'Swatch',
        },
        attributes:
          typeof props.isFirstProductRecommendation === 'undefined'
            ? []
            : props.isFirstProductRecommendation
            ? ['primarySwatch']
            : ['secondarySwatch'],
        requestArgs: {
          client_timestamp: Math.round(Date.now() / 1000),
          url: window.location.href,
        },
        eventData: {
          type: 'click',
          subtype: 'shade_selector',
          contents: [],
        },
        nonce: EnterpriseCtx.metricNonce,
      }),
    })
  }

  const onDropdownClick = () => {
    transmit({
      type: 'columbo',
      payload: getColumboEventData({
        propertyArgs,
        argumentsObj: {
          '0': 'Accessibility',
          '1': 'Clicked',
          '2': 'Dropdown',
        },
        attributes:
          typeof props.isFirstProductRecommendation === 'undefined'
            ? []
            : props.isFirstProductRecommendation
            ? ['primaryDropdown']
            : ['secondaryDropdown'],
        requestArgs: {
          client_timestamp: Math.round(Date.now() / 1000),
          url: window.location.href,
        },
        eventData: {
          type: 'click',
          subtype: 'product_variations_dropdown',
          contents: [],
        },
        nonce: EnterpriseCtx.metricNonce,
      }),
    })
  }

  const onLinkedSkuOptionChange = (option: string) => {
    if (!props.product?.linkedOn) return

    if (option === props.product.linkedOn) {
      transmit({
        type: 'columbo',
        payload: getColumboEventData({
          propertyArgs,
          argumentsObj: {
            '0': 'Accessibility',
            '1': 'Clicked',
            '2': 'LinkedSkuOption',
          },
          requestArgs: {
            client_timestamp: Math.round(Date.now() / 1000),
            url: window.location.href,
          },
          eventData: {
            type: 'click',
            subtype: 'linkedSKUOption',
            contents: [],
          },
          nonce: EnterpriseCtx.metricNonce,
        }),
      })
    }
  }

  const hullabalookOnProductExists = props.product?.hullabalookOnProduct

  const quickBuySelectedSku = !props.preselectVariant
    ? props.product?.sku
    : props.selectedVariant?.sku

  const quickBuyClickHandler = useQuickBuyClickHandler(
    quickBuySelectedSku || '',
    false,
    undefined,
    undefined,
    props.selectedOptions,
    setSelectedOptionsCallback,
    props.variantChangedCallback,
    setOptionsCallback,
    true,
    enableMobilePDPStickyAddToBasket,
  )

  const quickBuyModal = (event?: React.MouseEvent<Element, MouseEvent>) => {
    if (event) {
      event.stopPropagation()
      setDisplayError(false)
      quickBuyClickHandler(event)
    }
  }

  const getItemCategoryString = (product?: ProductPageData) => {
    if (!product) {
      return
    }

    const itemCategories = product?.content?.find(
      (item) => item?.key === 'productCategory',
    )

    if (itemCategories?.value.__typename === 'ProductContentStringListValue') {
      const stringListValue = (itemCategories.value as {
        __typename: 'ProductContentStringListValue'
        stringListValue: string[]
      }).stringListValue

      return stringListValue.join('/')
    }
    return product.breadcrumbs
      ?.filter((breadcrumb) => breadcrumb.url !== '/')
      .map((breadcrumb) => breadcrumb.text)
      .slice(0, -1)
      .join('/')
  }

  const getProductDataForGA4EnhancedEcom = (
    selectedVariant: Variant,
    product?: ProductPageData,
  ) => {
    if (!useGA4EnhancedEcom) {
      return
    }
    const shouldUseExternalIdentifier =
      useExternalIdentifier || useExternalIdentifierInSchema
    return {
      index: 0,
      item_id:
        shouldUseExternalIdentifier && product?.externalIdentifier
          ? product?.externalIdentifier
          : selectedVariant?.sku?.toString(),
      item_external_id:
        product.externalIdentifier || selectedVariant?.sku?.toString(),
      item_name: selectedVariant?.title,
      item_list_name: product?.title || selectedVariant?.title,
      price: selectedVariant?.price?.price.amount,
      item_brand: product?.brand?.name || '',
      item_category: getItemCategoryString(props.product),
      item_variant:
        shouldUseExternalIdentifier && selectedVariant?.externalIdentifier
          ? selectedVariant?.externalIdentifier
          : selectedVariant?.sku?.toString(),
      item_currency: currency,
      item_page: InteractionLocation.PDP,
      quantity: selectedQuantity,
      discount: calculateDiscount(
        selectedVariant?.price?.rrp?.amount,
        selectedVariant?.price?.price?.amount,
      ),
    }
  }

  const ProductOptionsRendererComponent = (
    <ProductOptionRenderer
      setSelectedOptionsCallback={(options) => {
        setDisplayError(false)
        props.setSelectedOptionsCallback &&
          props.setSelectedOptionsCallback(options)
      }}
      updateSelectedVariant={(variant) => {
        // TODO REBUILD-8692 fix type
        props.variantChangedCallback(variant)
        updateVariantsForGA4(true)
      }}
      isVariantsUpdatedForGA4={isVariantsUpdatedForGA4}
      updateVariantsForGA4={updateVariantsForGA4}
      sortedOptionKeys={props.sortedOptionKeys}
      masterProductSku={props.masterProductSku}
      options={options}
      setOptionsCallback={setOptionsCallback}
      selectedOptions={selectedOptions}
      displayError={displayError}
      setDisplayError={setDisplayError}
      notifyWhenInStock={props.notifyWhenInStock}
      forceDropDowns={props.forceDropDowns}
      swatchShape={props.swatchShape}
      showSwatches={props.showSwatches}
      labelCustomComponent={props.labelCustomComponent}
      isFirstProductRecommendation={props.isFirstProductRecommendation}
      onDropdownClick={onDropdownClick}
      onColourSwatchClick={onColourSwatchClick}
      productDataForGA4EnhancedEcom={getProductDataForGA4EnhancedEcom(
        props.selectedVariant,
        props.product,
      )}
      onLinkedSkuOptionChange={onLinkedSkuOptionChange}
      queryFullVariantData={props.queryFullVariantData}
      preselectVariant={props.preselectVariant}
      linkedOn={props.linkedOn ?? props.product?.linkedOn}
      setPersonalisationImages={setPersonalisationImages}
      disableColorDropdown={props.disableColorDropdown}
      shoeSizeMeEnabled={!!hasShoeSizeMeId}
      product={props.product}
    />
  )

  const updateImageData = () => {
    if (!personalisationImages) {
      return null
    }

    const selectedImageData =
      personalisationImages[selectedOptions?.selectedImageSwatch]
    setPersonalisationImagesContext(selectedImageData)

    return selectedImageData
  }

  const getAddProductToBasketHandler = (event) => {
    return horizonFeatures?.includes(Feature.SubscribeAndSave) &&
      props.activeTabIndex === 1 &&
      props.selectedVariant?.subscriptionContracts?.length
      ? addSubscriptionContractToBasket()
      : props.selectedVariant?.personalisationData &&
        Object.keys(props.selectedVariant.personalisationData).length &&
        activePersonalisationTabIndex === 1
      ? addPersonalisedProductToBasket()
      : enableMobilePDPStickyAddToBasket &&
        addToBasketIsSticky &&
        enableQuickbuyOnlyOnMobile
      ? quickBuyModal(event)
      : addProductToBasket()
  }

  const getSelectedContractPrice = (
    contracts: SubscriptionContract[] | undefined,
    selectedId: string,
    defaultPrice?: string,
  ) => {
    return (
      contracts?.find((contract) => contract.id === selectedId)?.initialPrice
        ?.price?.displayValue ||
      defaultPrice ||
      'N/A'
    )
  }

  const selectedContractDiscountedPrice = getSelectedContractPrice(
    props.selectedVariant?.subscriptionContracts,
    selectedFrequencyId,
    props.selectedVariant?.price?.price.displayValue,
  )

  const addProductToBasket = React.useCallback(() => {
    if (!props.selectedVariant?.enableAddToBasket) {
      setDisplayError(true)
      return
    }
    setDisplayError(false)
    addToBasket(
      [
        {
          sku: props.selectedVariant?.sku.toString(),
          quantity: selectedQuantity,
        },
      ],
      {
        fromRecommendations: false,
        location: InteractionLocation.PDP,
      },
    )
  }, [props.selectedVariant, selectedQuantity, addToBasket])

  const showAddToBasket = props.selectedVariant?.notifyWhenInStockEnabled
    ? props.selectedVariant?.inStock || false
    : true
  const [, setBasketId] = basketIdRef.value

  const [addToBaskedWithContracts] = useMutation<
    {
      addProductToBasketWithSubscriptionContract: AddBasketWithContractType
    },
    MutationAddProductToBasketWithSubscriptionContractArgs
  >(ADD_TO_BASKET_WITH_CONTRACT, {
    fetchPolicy: 'no-cache',
    onError: (error) => {
      logger.warn(
        `[AddToBasketWithContracts]: Failed to add to basket ${error.message}`,
      )
    },
    onCompleted: (data) => {
      if (data) {
        setBasketId(
          data.addProductToBasketWithSubscriptionContract
            .addProductToBasketWithSubscriptionContract.id,
        )
        presentAddedToBasketModal?.current(
          String(props.selectedVariant?.sku),
          selectedQuantity,
        )
      }
    },
  })

  const [addToBasketPersonalisedProducts] = useMutation<
    {
      addPersonalisedProductToBasket: AddBasketPersonalisedProduct
    },
    MutationAddPersonalisedProductToBasketArgs
  >(ADD_TO_BASKET_PERSONALISED_PRODUCTS, {
    fetchPolicy: 'no-cache',
    onError: (error) => {
      logger.warn(
        `[AddToBasketWithPersonalisedProducts]: Failed to add to basket ${error.message}`,
      )
    },
    onCompleted: (data) => {
      if (data) {
        setBasketId(
          data.addPersonalisedProductToBasket.addPersonalisedProductToBasket.id,
        )
        presentAddedToBasketModal?.current(
          String(props.selectedVariant?.sku),
          selectedQuantity,
        )
      }
    },
  })

  const addPersonalisedProductToBasket = () => {
    if (Object.values(personalisationData).includes('')) {
      return
    }
    setIsPersonalisationActive(true)
    setVariantSku(props.selectedVariant?.sku)
    setPersonalisationFields(props.selectedVariant?.personalisationFields)

    if (enableProductPersonalisationInterface && isPersonalisationActive) {
      window.location.href = personalisationUrl
    } else {
      addToBasketPersonalisedProducts({
        variables: {
          basketId: basket?.id,
          sku: props.selectedVariant?.sku.toString(),
          quantity: selectedQuantity,
          settings: {
            currency: currency || ('' as Currency),
            shippingDestination: shippingDestination || ('' as Country),
          },
          personalisationValues: {
            fieldSubmissionList: Object.keys(personalisationData).map(
              (key) => ({
                name: key,
                value: personalisationData[key],
              }),
            ),
          },
        },
      })
    }
  }

  const addSubscriptionContractToBasket = () => {
    addToBaskedWithContracts({
      variables: {
        basketId: basket?.id,
        sku: props.selectedVariant?.sku.toString(),
        quantity: selectedQuantity,
        contractId: selectedFrequencyId,
        settings: {
          currency: currency || ('' as Currency),
          shippingDestination: shippingDestination || ('' as Country),
        },
      },
    })
  }
  const displayPurchaseOptions: boolean =
    horizonFeatures?.includes(Feature.Subscriptions) || false

  const checkIfSubscriptionBasketEmpty =
    horizonFeatures?.includes(Feature.SubscribeAndSave) &&
    basket?.items?.length === 0

  const productInBasketWithContract =
    !!basketProductSku && props.activeTabIndex === 0

  const productInBasketWithoutContract =
    basketProductSku === null &&
    props.activeTabIndex === 1 &&
    !checkIfSubscriptionBasketEmpty

  const wishlistPDPButtonStyle =
    theme.pageTheme?.productOptions.addToWishlistButton.style || undefined

  const pricing = getPrice(
    props.selectedVariant,
    defaultCurrency,
    defaultLocale,
    props.activeTabIndex,
    selectedContractDiscountedPrice,
  )

  const constituentProductCta = getConstituentProductCta(
    { buyNowText: i18nText.buyNow, infoMessage: i18nText.buyNowInfoMessage },
    props.product.constituentProduct,
  )

  useSessionStorage(
    isPersonalisationActive,
    variantSku,
    personalisationFields,
    [
      isPersonalisedProduct,
      isPersonalisationActive,
      variantSku,
      personalisationFields,
    ],
  )

  const QuantityAndBasketComponent = (
    <span>
      {showQuantity && (
        <QuantityText
          quantityTextLabelStyle={
            theme.pageTheme.productOptions.quantitySelector?.quantityLabelText
              ?.quantityLabel
          }
        >
          {i18nText.quantitySelector.quantityLabel}
        </QuantityText>
      )}
      <AddToBasketContainer
        enableQuantity={props.enableQuantity}
        hasPaddingBottom={pushNotification}
        flexDirection={
          theme?.pageTheme?.productOptions?.addToBasketContainer?.direction
        }
      >
        {showQuantity && (
          <QuantitySelectorContainer
            margin={
              theme?.pageTheme?.productOptions?.quantitySelector?.margin || {
                top: '0',
                right: spacing(3),
                bottom: spacing(3),
                left: '0',
              }
            }
          >
            <QuantitySelector {...quantitySelectorProps} />
          </QuantitySelectorContainer>
        )}

        {props.selectedVariant?.maxPerOrder && quantityInBasket > 0 ? (
          <QuantityPlatformMessage
            type="info"
            text={maxQuantityBasketItemMessageText}
            data-testid="quantity-basket-item"
          />
        ) : null}
        {showSetupPushNotificationText && (
          <OutOfStockText
            data-testid="out-of-stock-message"
            content={notificationOptInText}
          />
        )}
        {enableClickAndCollect ? (
          <FulfilmentMethodIconsRenderer
            {...fulfilmentMethodIconsProps}
            deliveryFulfilmentTextStyle={
              theme?.pageTheme?.productOptions?.deliveryFulfilmentTextStyle
            }
          />
        ) : null}
        {(clickAndCollectAvailable || props.selectedVariant?.isCheckStock) && (
          <ClickAndCollectButtonRenderer
            text={i18nText.clickAndCollect.clickAndCollectLinkText}
            disabled={!props.selectedVariant?.inStock || maxQuantity < 1}
            emphasis="low"
            dataTestId="click-and-collect-info-button"
            showIcon
            presentClickAndCollectModalProps={{
              sku: props.selectedVariant?.sku,
              image: props.selectedVariant?.images?.[0]?.largeProduct || '',
              title: props.selectedVariant?.title,
              isProductPage: true,
              quantity: selectedQuantity,
              externalIdentifier: props.product?.externalIdentifier,
            }}
          />
        )}
        <StyledButtonContainer
          isFullWidth={pushNotification}
          ref={addToBasketButtonContainerRef}
        >
          {pushNotification ? (
            <PushNotifyWhenInStock
              updateProductVariants={updateProductVariants}
              isOptedInForNotification={
                optedInVariants[props.selectedVariant?.sku]
              }
              sku={props.selectedVariant?.sku}
            />
          ) : (
            showAddToBasket &&
            homeDeliveryAvailable && (
              <AddToBasketButton
                selectedVariant={props.selectedVariant}
                isSticky={
                  props.enableMobilePDPStickyAddToBasket &&
                  addToBasketIsSticky &&
                  hasScrolled
                }
                pricing={pricing}
                priceOnSticky={props.enablePriceOnMobilePDPStickyAddToBasket}
                priceTheme={theme.pageTheme.productSummary.price}
                maxQuantity={maxQuantity}
                selectedQuantity={selectedQuantity}
                addToBasketText={getAddToBasketText({
                  inStock: !(
                    showOOSMessagePDP &&
                    !props.selectedVariant?.inStock &&
                    props.preselectVariant
                  ),
                  addToBasketButton: i18nText.addToBasketButton,
                  preorder: props.selectedVariant?.product?.preorder,
                  personalisedProduct: !!props.selectedVariant
                    ?.personalisationFields?.length,
                })}
                enablePreselectedProductVariant={props?.preselectVariant}
                addProductToBasketHandler={getAddProductToBasketHandler}
                disabled={
                  (horizonFeatures?.includes(Feature.Subscriptions) &&
                    productInBasketWithContract) ||
                  (productInBasketWithoutContract &&
                    !!props.selectedVariant?.subscriptionContracts?.length) ||
                  // TODO REBUILD-8908 ensure this disabled logic is correct
                  (!props.preselectVariant &&
                  !props.selectedVariant?.enableAddToBasket
                    ? false
                    : !props.selectedVariant?.inStock)
                }
                outOfStockPDPButtonStyle={
                  theme.pageTheme.productOptions.addToBasketContainer
                    ?.outOfStockButton?.style
                }
                offLabel={i18nText.offLabel}
              />
            )
          )}
          {addToBasketWishlist && (
            <WishlistButtonWrapper hasBorderOutline={pushNotification}>
              <WishlistButtonRenderer
                selectedVariant={props.selectedVariant || props.defaultVariant}
                buttonWithoutText={true}
                buttonStyle={wishlistButtonStyle}
                buttonType={WishlistButtonType.SMALL}
                itemCategories={props.product?.content}
                brandName={props.product?.brand?.name}
                externalIdentifier={props.product?.externalIdentifier}
              />
            </WishlistButtonWrapper>
          )}
        </StyledButtonContainer>
        {showHullablookCTAButton &&
          showHullabalookRecommendations &&
          hullabalookRecsExperienceLabel &&
          hullabalookOnProductExists && (
            <HullabalookButton emphasis="medium" renderedAs="a" href={'#hulla'}>
              <StyledHangerIcon />
              {i18nText.buildYourOufitMessage}
            </HullabalookButton>
          )}
        {(clickAndCollectAvailable || props.selectedVariant?.isCheckStock) && (
          <ClickAndCollectButtonRenderer
            dataTestId="click-and-collect-button"
            emphasis="high"
            disabled={!props.selectedVariant?.inStock || maxQuantity < 1}
            presentClickAndCollectModalProps={{
              sku: props.selectedVariant?.sku,
              image: props.selectedVariant?.images?.[0]?.largeProduct || '',
              title: props.selectedVariant?.title,
              isProductPage: true,
              quantity: selectedQuantity,
              externalIdentifier: props.product?.externalIdentifier,
            }}
            text={
              props.selectedVariant?.isCheckStock
                ? i18nText.clickAndCollect.clickAndCollectCheckStoreStockText
                : i18nText.clickAndCollect.clickAndCollectButtonText
            }
            isFullWidth
          />
        )}
      </AddToBasketContainer>
    </span>
  )

  const showWishlist = !addToBasketWishlist && !constituentProductCta
  const showQuantityAndBasketComponent =
    !props.selectedVariant?.personalisationFields && !constituentProductCta

  return (
    <React.Fragment>
      {displayPurchaseOptions &&
        (props.product?.hasSubscriptions ||
          props.product?.subscriptionData?.isSubscription) && (
          <AuroraPurchaseOptions
            isSubscription={!!props.product?.subscriptionData.isSubscription}
            subscribeOptions={
              props.selectedVariant?.subscriptionData?.subscribeOptions
            }
            subscriptionPaymentType={
              props.selectedVariant?.subscriptionPaymentType
            }
            subscriptionContracts={props.selectedVariant?.subscriptionContracts}
            onOptionChange={(option, _) => {
              onLinkedSkuOptionChange(option)
            }}
            setActiveTabIndex={props.setActiveTabIndex}
            activeTabIndex={props.activeTabIndex}
            selectedFrequencyId={selectedFrequencyId}
            setSelectedFrequencyId={setSelectedFrequencyId}
            variants={props.variants}
            setSelectedOptionsCallback={props.setSelectedOptionsCallback}
            selectedOptions={props.selectedOptions}
            updateSelectedVariant={props.variantChangedCallback}
            selectedVariant={props?.selectedVariant}
            defaultVariant={props.defaultVariant}
            onLinkedSkuOptionChange={onLinkedSkuOptionChange}
            enableSubscribeAndSaveInlinePricing={
              props.enableSubscribeAndSaveInlinePricing
            }
            displaysPrices={{
              oneTimePurchaseDisplayPrice:
                props.selectedVariant?.price?.price.displayValue,
              subscriptionDisplayPrice: selectedContractDiscountedPrice,
            }}
          />
        )}
      {props.selectedVariant?.personalisationFields ? (
        <PersonalisationToggle
          activePersonalisationTabIndex={activePersonalisationTabIndex}
          setActivePersonalisationTabIndex={setActivePersonalisationTabIndex}
          showClassicContent={
            theme.pageTheme.productPersonalisation?.showClassicContent
          }
          classicContent={
            <React.Fragment>
              {ProductOptionsRendererComponent}
              {QuantityAndBasketComponent}
            </React.Fragment>
          }
          personalisedContent={
            <React.Fragment>
              {ProductOptionsRendererComponent}
              <PersonalisationForm
                selectedVariant={props.selectedVariant}
                onImageChange={setSelectedOptions}
                imageData={updateImageData()}
                personalisationData={personalisationData}
                selectedImageSwatch={selectedOptions?.selectedImageSwatch}
                personalisationDataCallback={(data: PersonalisationData) => {
                  data && setPersonalisationData(data)
                }}
              />
              {QuantityAndBasketComponent}
            </React.Fragment>
          }
        />
      ) : (
        !props.product?.subscriptionData?.isSubscription &&
        ProductOptionsRendererComponent
      )}
      {showSetupPushNotificationText && (
        <OutOfStockText
          data-testid="out-of-stock-message"
          content={notificationOptInText}
        />
      )}
      {displayPriceAboveQuantity && (
        <React.Fragment>
          <FreeDeliveryIndicator />
          <PriceContainer reduceBottomMargin={papMessageTopPosition}>
            <Price
              priceTheme={{
                price: theme.pageTheme.productSummary.price.price,
                rrp: theme.pageTheme.productSummary.price.rrp,
                savings: theme.pageTheme.productSummary.price.savings,
                priceWithRRP: theme.pageTheme.productSummary.price.priceWithRRP,
              }}
              priceDirection={theme.pageTheme.productSummary.price.direction}
              displayRRPText={
                theme.pageTheme.productSummary.price.displayRRPText
              }
              pricing={pricing}
              i18nText={{
                rrpLabel: i18nText.rrpLabel,
                saveLabel: i18nText.saveLabel,
                offLabel: i18nText.offLabel,
              }}
            />
          </PriceContainer>
          {!LoyaltyEnabled && <Separator withSpacing />}
        </React.Fragment>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {showQuantityAndBasketComponent && QuantityAndBasketComponent}
        {productInBasketWithContract && (
          <SubscriptionInfoMessage
            text={i18nText.productWithSubscriptionContracts}
            type="info"
          />
        )}
        {productInBasketWithoutContract &&
          !!props.selectedVariant?.subscriptionContracts?.length && (
            <SubscriptionInfoMessage
              text={i18nText.alreadyInBasketAsSubscription}
              type="info"
            />
          )}
      </div>
      {constituentProductCta}
      {showWishlist && (
        <WishlistButtonWrapper hasBorderOutline={pushNotification}>
          <WishlistButtonRenderer
            selectedVariant={props.selectedVariant || props.defaultVariant}
            buttonStyle={wishlistButtonStyle}
            buttonType={WishlistButtonType.TEXT}
            wishlistPDPButtonStyle={wishlistPDPButtonStyle}
            itemCategories={props.product?.content}
            brandName={props.product?.brand?.name}
            externalIdentifier={props.product?.externalIdentifier}
          />
        </WishlistButtonWrapper>
      )}
    </React.Fragment>
  )
}
